import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import { v4 as uuidv4 } from 'uuid';
import slugify from 'slugify';

function LandingPage({ addToCart, cartItem, getAuthHeaders, setCart }) {
  const { landingRoute } = useParams();
  const navigate = useNavigate();
  const [eventId] = useState(uuidv4());

  // Landing page data
  const [landingData, setLandingData] = useState(null);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Header data
  const [headerData, setHeaderData] = useState(null);

  // Product states
  const [activeImage, setActiveImage] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  // Selected products for checkout
  const [selectedProducts, setSelectedProducts] = useState([]);

  // UI states
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [currentReviewImageIndex, setCurrentReviewImageIndex] = useState(0);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedModalProduct, setSelectedModalProduct] = useState(null);

  // Checkout form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    notes: '',
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [deliveryOption, setDeliveryOption] = useState('');
  const [selectedDivisionName, setSelectedDivisionName] = useState('');
  const [selectedDistrictName, setSelectedDistrictName] = useState('');
  const [selectedUpazilaName, setSelectedUpazilaName] = useState('');

  // Address API states
  const [divisions, setDivisions] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [districts, setDistricts] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [upazilas, setUpazilas] = useState([]);
  const [selectedUpazilaId, setSelectedUpazilaId] = useState('');

  const [isValid, setIsValid] = useState(false);
  const [message, setMessage] = useState('');
  const [isOrderButtonEnabled, setIsOrderButtonEnabled] = useState(false);
  const [isOrderPlacing, setIsOrderPlacing] = useState(false);
  const [mainFormError, setMainFormError] = useState('');

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [bkashTrxId, setBkashTrxId] = useState('');
  const [bkashPhoneNo, setBkashPhoneNo] = useState('');
  const [paymentDetailsConfirmed, setPaymentDetailsConfirmed] = useState(false);
  const [paymentModalError, setPaymentModalError] = useState('');

  const API_BASE_URL = 'https://bdapi.vercel.app/api/v.1';
  const FLASK_API_URL = 'https://dynamicgadgets-server.sparkx-ai.shop';

  const bKashAgentNumber = '017XXXXXXXX';
  const nagadAgentNumber = '018XXXXXXXX';

  const [orderId] = useState(uuidv4());
  const clientId = localStorage.getItem('client_id');

  const hasFiredRef = useRef(false);
  const reviewScrollRef = useRef(null);
  const checkoutRef = useRef(null);

  // Open product modal
  const handleProductImageClick = (relatedProduct) => {
    setSelectedModalProduct(relatedProduct);
    setShowProductModal(true);
  };

  // Close product modal  
  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setSelectedModalProduct(null);
  };

  // Fetch landing page and product data
  useEffect(() => {
    const fetchLandingPageData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${FLASK_API_URL}/api/landing-page`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ route: landingRoute }),
        });

        if (!response.ok) {
          if (response.status === 404) {
            navigate('/404');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setLandingData(data.landingPage);
        setProduct(data.product);
        setRelatedProducts(data.relatedProducts || []);

        if (data.headerData) {
          setHeaderData(data.headerData);
        }

        if (data.product) {
          setActiveImage(data.landingPage.imageUrls?.[0] || data.product.image);
          setSelectedColor(data.product.colors?.[0]?.hex || '');
          setSelectedSize(data.product.sizes?.[0] || '');

          setSelectedProducts([{
            ...data.product,
            selectedQuantity: 1,
            selectedSize: data.product.sizes?.[0] || '',
            selectedColor: data.product.colors?.[0]?.hex || '',
          }]);
        }
      } catch (err) {
        console.error('Error fetching landing page:', err);
        setError('Page could not be loaded. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (landingRoute) {
      fetchLandingPageData();
    }
  }, [landingRoute, navigate]);

  // Auto-scroll review text cards
  useEffect(() => {
    if (!reviewScrollRef.current) return;

    const scrollContainer = reviewScrollRef.current;
    let scrollInterval;

    const startScrolling = () => {
      scrollInterval = setInterval(() => {
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
          scrollContainer.scrollLeft = 0;
        } else {
          scrollContainer.scrollLeft += 1;
        }
      }, 30);
    };

    startScrolling();

    return () => {
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [landingData]);

  // Facebook Pixel tracking
  useEffect(() => {
    if (hasFiredRef.current || !product || !landingData) return;

    function getFbClickId() {
      const match = document.cookie.match(/_fbc=([^;]*)/);
      return match ? match[1] : null;
    }

    function getFbBrowserId() {
      const match = document.cookie.match(/_fbp=([^;]*)/);
      return match ? match[1] : null;
    }

    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId();
    const fbp = getFbBrowserId();
    const userData = { fbc, fbp, external_id: externalId };

    ReactPixel.track('ViewContent', {
      content_name: product.name,
      content_category: product.category,
      content_ids: [product.productId],
      content_type: 'product',
      value: product.price,
      currency: 'BDT',
      event_id: eventId
    }, userData);

    ReactGA.event('view_item', {
      currency: 'BDT',
      value: product.price,
      items: [{
        item_id: product.productId,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: 1
      }],
      event_id: eventId,
    });

    hasFiredRef.current = true;
  }, [product, landingData, eventId]);

  // Fetch divisions
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/division`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) setDivisions(data.data);
      } catch (err) {
        console.error('Failed to load divisions:', err);
      }
    };
    fetchDivisions();
  }, []);

  // Fetch districts when division changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (!selectedDivisionId) {
        setDistricts([]);
        setSelectedDistrictId('');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/district/${selectedDivisionId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) {
          setDistricts(data.data);
          setSelectedDistrictId('');
        }
      } catch (err) {
        console.error('Failed to load districts:', err);
      }
    };
    fetchDistricts();
  }, [selectedDivisionId]);

  // Fetch upazilas when district changes
  useEffect(() => {
    const fetchUpazilas = async () => {
      if (!selectedDistrictId) {
        setUpazilas([]);
        setSelectedUpazilaId('');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/upazilla/${selectedDistrictId}`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data && data.data) {
          setUpazilas(data.data);
          setSelectedUpazilaId('');
        }
      } catch (err) {
        console.error('Failed to load upazilas:', err);
      }
    };
    fetchUpazilas();
  }, [selectedDistrictId]);

  // Handle payment method modal
  useEffect(() => {
    if ((selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && !paymentDetailsConfirmed) {
      setShowPaymentModal(true);
    } else {
      setShowPaymentModal(false);
    }

    if (selectedPaymentMethod === 'cashOnDelivery') {
      setPaymentDetailsConfirmed(true);
      setBkashTrxId('');
      setBkashPhoneNo('');
    }
  }, [selectedPaymentMethod, paymentDetailsConfirmed]);

  // Validate order button
  useEffect(() => {
    const isFormFilled = formData.name.trim() !== '' &&
      formData.phone.trim() !== '' &&
      formData.address.trim() !== '';
    const isPaymentSelected = selectedPaymentMethod !== '';
    const isDeliveryOptionSelected = deliveryOption !== '';
    const hasProducts = selectedProducts.length > 0;

    let isPaymentConfirmed = false;
    if (selectedPaymentMethod === 'cashOnDelivery') {
      isPaymentConfirmed = true;
    } else if (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') {
      isPaymentConfirmed = paymentDetailsConfirmed;
    }

    setIsOrderButtonEnabled(
      isFormFilled &&
      isPaymentSelected &&
      isDeliveryOptionSelected &&
      isPaymentConfirmed &&
      selectedDivisionId.length > 0 &&
      selectedDistrictId.length > 0 &&
      isValid &&
      hasProducts
    );
  }, [formData, selectedPaymentMethod, paymentDetailsConfirmed, deliveryOption, selectedDivisionId, selectedDistrictId, isValid, selectedProducts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'phone') {
      const checkValidity = (num) => {
        const digitsOnly = num.replace(/[^0-9+]/g, '');
        if (digitsOnly === '') {
          setIsValid(false);
          setMessage('Phone number cannot be empty.');
          return;
        }
        if (digitsOnly.startsWith('+88')) {
          const numberWithoutCountryCode = digitsOnly.slice(1);
          if (numberWithoutCountryCode.length === 13) {
            if (/^\d+$/.test(numberWithoutCountryCode)) {
              setIsValid(true);
              setMessage('Valid phone number.');
            } else {
              setIsValid(false);
              setMessage('Use digits only.');
            }
          } else {
            setIsValid(false);
            setMessage('Provide 13 digits including +88.');
          }
        } else {
          if (digitsOnly.length === 11) {
            if (/^\d+$/.test(digitsOnly)) {
              setIsValid(true);
              setMessage('Valid phone number.');
            } else {
              setIsValid(false);
              setMessage('Use digits only.');
            }
          } else {
            setIsValid(false);
            setMessage('Provide 11-digit phone number.');
          }
        }
      };
      checkValidity(value);
    }
  };

  const handleDivisionChange = (event) => {
    const selectedId = event.target.value;
    setSelectedDivisionId(selectedId);
    const foundDivision = divisions.find(division => division.id === selectedId);
    if (foundDivision) {
      setSelectedDivisionName(foundDivision.bn_name);
    } else {
      setSelectedDivisionName('');
    }
  };

  const handleDistrictChange = (event) => {
    const selectedId = event.target.value;
    setSelectedDistrictId(selectedId);
    const foundDistrict = districts.find(district => district.id === selectedId);

    if (foundDistrict) {
      setSelectedDistrictName(foundDistrict.bn_name);

      if (foundDistrict.bn_name === 'ঢাকা') {
        setDeliveryOption('insideDhaka');
      } else if (foundDistrict.bn_name !== '') {
        setDeliveryOption('outsideDhaka');
      }
    } else {
      setSelectedDistrictName("");
      setDeliveryOption('');
    }
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
    setPaymentDetailsConfirmed(false);
    setMainFormError('');
  };

  const handleDeliveryOptionChange = (option) => {
    setDeliveryOption(option);
    setMainFormError('');
  };

  const handlePaySubmit = () => {
    setPaymentModalError('');
    if (!bkashTrxId.trim()) {
      setPaymentModalError('TrxID cannot be empty.');
      return;
    }
    if (!/^\d{11}$/.test(bkashPhoneNo)) {
      setPaymentModalError('Provide valid 11-digit mobile number.');
      return;
    }
    setPaymentDetailsConfirmed(true);
    setShowPaymentModal(false);
  };

  const handleAddRelatedProduct = (relatedProduct) => {
    const isAlreadySelected = selectedProducts.some(p => p.productId === relatedProduct.productId);

    if (isAlreadySelected) {
      setSelectedProducts(selectedProducts.filter(p => p.productId !== relatedProduct.productId));
    } else {
      setSelectedProducts([
        ...selectedProducts,
        {
          ...relatedProduct,
          selectedQuantity: 1,
          selectedSize: relatedProduct.sizes?.[0] || '',
          selectedColor: relatedProduct.colors?.[0]?.hex || '',
        }
      ]);
    }
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.productId !== productId));
  };

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) return;

    setSelectedProducts(selectedProducts.map(p =>
      p.productId === productId
        ? { ...p, selectedQuantity: newQuantity }
        : p
    ));
  };

  const scrollToCheckout = () => {
    checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getDeliveryChargeInfo = () => {
    let charge = 0;
    let display = '';

    if (!landingData) return { chargeForApi: 0, displayCharge: 'Select option' };

    if (deliveryOption === 'insideDhaka') {
      charge = landingData.insideDhaka || 60;
      display = `৳${charge}`;
    } else if (deliveryOption === 'outsideDhaka') {
      charge = landingData.outsideDhaka || 120;
      display = `৳${charge}`;
    } else if (deliveryOption === 'subarea') {
      charge = landingData.subarea || 100;
      display = `৳${charge}`;
    } else {
      display = 'Select option';
    }

    return { chargeForApi: charge, displayCharge: display };
  };

  const { chargeForApi, displayCharge } = getDeliveryChargeInfo();

  const calculateSubtotal = () => {
    return selectedProducts.reduce((total, product) => {
      return total + (product.price * product.selectedQuantity);
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + chargeForApi;
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setMainFormError('');

    if (!isOrderButtonEnabled) {
      setMainFormError('Please fill all required information.');
      return;
    }

    if (selectedProducts.length === 0) {
      setMainFormError('Please select at least one product.');
      return;
    }

    setIsOrderPlacing(true);

    function getFbClickId() {
      const match = document.cookie.match(/_fbc=([^;]*)/);
      return match ? match[1] : null;
    }

    function getFbBrowserId() {
      const match = document.cookie.match(/_fbp=([^;]*)/);
      return match ? match[1] : null;
    }

    const fbc = getFbClickId();
    const fbp = getFbBrowserId();
    const eventTime = Math.floor(Date.now() / 1000);

    const cartItems = selectedProducts.map(product => ({
      productId: product.productId,
      name: product.name,
      quantity: product.selectedQuantity,
      price: product.price,
      image: product.image,
      originalPrice: product.originalPrice || product.price,
      selectedSize: product.selectedSize || null,
      selectedColor: product.selectedColor ? {
        name: product.colors?.find(c => c.hex === product.selectedColor)?.name || ''
      } : null,
      PAW: typeof product.PAW === 'number' ? product.PAW * product.selectedQuantity : 0,
      PCW: typeof product.PCW === 'number' ? product.PCW * product.selectedQuantity : 0,
      buyingPrice: typeof product.buyingPrice === 'number' ? product.buyingPrice : 0,
    }));

    const subtotalAmount = calculateSubtotal();
    const totalPAW = selectedProducts.reduce((sum, p) => sum + (typeof p.PAW === 'number' ? p.PAW * p.selectedQuantity : 0), 0);
    const totalPCW = selectedProducts.reduce((sum, p) => sum + (typeof p.PCW === 'number' ? p.PCW * p.selectedQuantity : 0), 0);
    const totalRevenue = selectedProducts.reduce((sum, p) => {
      const revenue = (p.price - (p.buyingPrice || 0)) * p.selectedQuantity;
      return sum + revenue;
    }, 0);

    const orderSummary = {
      orderId: orderId,
      orderDate: new Date().toLocaleDateString(),
      orderTime: new Date().toLocaleTimeString(),
      eventTime: eventTime,
      customerInfo: formData,
      division: selectedDivisionName,
      district: selectedDistrictName,
      upazila: selectedUpazilaName || 'Not selected',
      cartItems: cartItems,
      subtotalAmount: subtotalAmount,
      discountAmount: 0,
      CustomerDeliveryCharge: chargeForApi,
      AdjustedCustomerDeliveryCharge: 0,
      totalAmount: calculateTotal(),
      paymentMethod: selectedPaymentMethod,
      paymentDetails: (selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && paymentDetailsConfirmed
        ? { trxId: bkashTrxId, phoneNo: bkashPhoneNo }
        : null,
      appliedCouponCode: null,
      status: 'Processing',
      reviewAdded: false,
      clientId: clientId,
      totalPAW: totalPAW,
      totalPCW: totalPCW,
      totalRevenue: totalRevenue,
      ActualTotalRevenue: totalRevenue,
      deliveryOption: deliveryOption,
      fbc: fbc,
      fbp: fbp,
      external_id: clientId,
    };

    try {
      const response = await fetch(`${FLASK_API_URL}/place_order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderSummary),
      });

      const data = await response.json();

      if (response.ok) {
        console.log('Order successfully placed:', data.message);
        setCart([]);
        navigate('/thank-you', { state: { orderSummary: data.order } });
      } else {
        setMainFormError(data.error || 'Failed to place order.');
        console.error('Failed to place order:', data.error);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      setMainFormError('Network error placing order.');
    } finally {
      setIsOrderPlacing(false);
    }
  };

  const getTruncatedDescription = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    const lines = text.split('\n').filter(line => line.trim() !== '');

    if (showFullDescription || lines.length <= 6) {
      return html;
    }

    const truncatedLines = lines.slice(0, 6).join('\n');
    return truncatedLines;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;

    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }

    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600" style={{ fontFamily: 'Assistant, -apple-system, sans-serif' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !landingData || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-xl" style={{ fontFamily: 'Assistant, -apple-system, sans-serif' }}>{error || 'Page not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{landingData.title} | ElevateMart</title>
        <meta name="description" content={landingData.shortDescription || product.description} />
        <meta property="og:title" content={landingData.title} />
        <meta property="og:description" content={landingData.shortDescription} />
        <meta property="og:image" content={landingData.imageUrls?.[0] || product.image} />
        <meta property="og:url" content={`https://www.dynamicgadgets.com.bd/${landingRoute}`} />
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: 'Assistant, -apple-system, sans-serif' }}>
        {/* Header Section */}
        {headerData && (
          <header className="bg-gray-900 text-white py-3 px-4 sticky top-0 z-50 shadow-md border-b border-gray-700">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                {headerData.logoUrl && (
                  <img
                    src={headerData.logoUrl}
                    alt={headerData.companyName || 'Logo'}
                    className="h-10 w-auto object-contain"
                  />
                )}
                {headerData.companyName && (
                  <span className="text-lg font-semibold hidden sm:inline-block">
                    {headerData.companyName}
                  </span>
                )}
              </div>

              {headerData.phone && (
                <div className="flex gap-2">
                  <a
                    href={`tel:${headerData.phone}`}
                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 px-3 sm:px-4 py-2 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="font-medium text-xs sm:text-base hidden sm:inline">{headerData.phone}</span>
                  </a>
                </div>
              )}
            </div>
          </header>
        )}

        {/* Hero Section with Product Details */}
        <div className="bg-white py-8 sm:py-12 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Product Images */}
              <div>
                <div className="sticky top-20">
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
                    <img
                      src={activeImage}
                      alt={product.name}
                      className="w-full h-[400px] sm:h-[500px] object-contain"
                    />
                  </div>

                  {/* Thumbnails */}
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {landingData.imageUrls && landingData.imageUrls.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImage(img)}
                        className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 border-2 rounded overflow-hidden hover:border-orange-500 transition-colors ${activeImage === img ? 'border-orange-500' : 'border-gray-300'
                          }`}
                      >
                        <img
                          src={img}
                          alt={`${product.name} - ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-normal text-gray-900 mb-4">
                  {product.name}
                </h1>

                <div className="flex items-baseline gap-3 sm:gap-4 mb-6 flex-wrap">
                  <span className="text-3xl sm:text-4xl font-normal text-red-700">
                    ৳{product.price}
                  </span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <>
                      <span className="text-xl sm:text-2xl text-gray-500 line-through">
                        ৳{product.originalPrice}
                      </span>
                      <span className="text-base sm:text-lg text-red-600 font-normal">
                        Save {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                      </span>
                    </>
                  )}
                </div>

                <div className={`text-base sm:text-lg font-normal mb-6 ${product.stock > 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {product.stock > 0 ? `✓ In Stock (${product.stock} available)` : '✗ Out of Stock'}
                </div>

                {/* Short Description */}
                {landingData.shortDescription && (
                  <div
                    className="text-sm sm:text-base mb-6 text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: landingData.shortDescription }}
                  />
                )}

                {/* Color Selection */}
                {product.isColorAvailable && product.colors && product.colors.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">
                      Color: {product.colors.find(c => c.hex === selectedColor)?.name || ''}
                    </h3>
                    <div className="flex gap-2 sm:gap-3 flex-wrap">
                      {product.colors.map((color, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedColor(color.hex)}
                          className={`flex items-center gap-2 px-3 sm:px-4 py-2 border-2 rounded-lg transition-all ${selectedColor === color.hex
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-300 hover:border-gray-400'
                            }`}
                        >
                          <div
                            className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border border-gray-300"
                            style={{ backgroundColor: color.hex }}
                          />
                          <span className="text-xs sm:text-sm font-medium">
                            {color.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Size Selection */}
                {product.isSizeAvailable && product.sizes && product.sizes.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">
                      Size: {selectedSize}
                    </h3>
                    <div className="flex gap-2 sm:gap-3 flex-wrap">
                      {product.sizes.map((size, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 sm:px-6 py-2 sm:py-3 border-2 rounded-lg transition-all font-medium ${selectedSize === size
                            ? 'border-orange-500 bg-orange-50 text-orange-600'
                            : 'border-gray-300 hover:border-gray-400'
                            }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Buy Now Button */}
                <button
                  onClick={scrollToCheckout}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Order Now
                </button>

                {/* WhatsApp Button */}
                <button
                  onClick={() => {
                    const message = `I want to know about ${product.name} https://www.dynamicgadgets.com.bd/${landingRoute}`;
                    const encodedMessage = encodeURIComponent(message);
                    window.open(`https://wa.me/+8801345688562?text=${encodedMessage}`, '_blank');
                  }}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 mt-3"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  Contact on WhatsApp
                </button>

                {/* Messenger Button */}
                <button
                  onClick={() => window.open('https://m.me/elevateemart', '_blank')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 mt-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.193 14.963l-3.056-3.259-5.963 3.259L10.733 8l3.13 3.259L19.752 8l-6.559 6.963z" />
                  </svg>
                  Chat on Messenger
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products Section */}
        {relatedProducts && relatedProducts.length > 0 && (
          <div className="bg-gray-50 py-8 sm:py-12 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Add More to Your Order
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-6">
                {relatedProducts.map((relatedProduct) => {
                  const isSelected = selectedProducts.some(p => p.productId === relatedProduct.productId);

                  return (
                    <div
                      key={relatedProduct.productId}
                      className={`bg-white rounded-lg shadow-md overflow-hidden border-2 transition-all ${isSelected ? 'border-orange-500 shadow-lg' : 'border-gray-200 hover:border-orange-300'
                        }`}
                    >
                      <div className="relative cursor-pointer" onClick={() => handleProductImageClick(relatedProduct)}>
                        <img
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-32 sm:h-48 object-contain bg-gray-50"
                        />
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-orange-500 text-white rounded-full p-1">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="p-2 sm:p-4">
                        <h3 className="text-xs sm:text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                          {relatedProduct.name}
                        </h3>
                        <div className="flex items-baseline gap-1 sm:gap-2 mb-2 sm:mb-3">
                          <span className="text-sm sm:text-xl font-bold text-red-700">
                            ৳{relatedProduct.price}
                          </span>
                          {relatedProduct.originalPrice && relatedProduct.originalPrice > relatedProduct.price && (
                            <span className="text-xs sm:text-sm text-gray-500 line-through">
                              ৳{relatedProduct.originalPrice}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddRelatedProduct(relatedProduct)}
                          className={`w-full py-1.5 sm:py-2 px-2 sm:px-4 rounded-lg text-xs sm:text-base font-medium transition-all ${isSelected
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'bg-orange-500 hover:bg-orange-600 text-white'
                            }`}
                        >
                          {isSelected ? 'Remove' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Product Details Modal */}
        {showProductModal && selectedModalProduct && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={handleCloseProductModal}
          >
            <div
              className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCloseProductModal}
                className="sticky top-4 right-4 float-right bg-red-500 hover:bg-red-600 text-white rounded-full p-2 z-10 shadow-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="p-6 sm:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={selectedModalProduct.image}
                      alt={selectedModalProduct.name}
                      className="w-full h-64 sm:h-96 object-contain"
                    />
                  </div>

                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                      {selectedModalProduct.name}
                    </h2>

                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="text-3xl font-bold text-red-700">
                        ৳{selectedModalProduct.price}
                      </span>
                      {selectedModalProduct.originalPrice && selectedModalProduct.originalPrice > selectedModalProduct.price && (
                        <>
                          <span className="text-xl text-gray-500 line-through">
                            ৳{selectedModalProduct.originalPrice}
                          </span>
                          <span className="text-base text-red-600 font-semibold">
                            {Math.round(((selectedModalProduct.originalPrice - selectedModalProduct.price) / selectedModalProduct.originalPrice) * 100)}% OFF
                          </span>
                        </>
                      )}
                    </div>

                    <div className={`text-lg font-semibold mb-4 ${selectedModalProduct.stock > 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {selectedModalProduct.stock > 0 ? `✓ In Stock (${selectedModalProduct.stock})` : '✗ Out of Stock'}
                    </div>

                    {selectedModalProduct.description && (
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">
                          Description:
                        </h3>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {selectedModalProduct.description}
                        </p>
                      </div>
                    )}

                    {selectedModalProduct.isColorAvailable && selectedModalProduct.colors && selectedModalProduct.colors.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">
                          Color:
                        </h3>
                        <div className="flex gap-2 flex-wrap">
                          {selectedModalProduct.colors.map((color, index) => (
                            <div key={index} className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg">
                              <div
                                className="w-5 h-5 rounded-full border border-gray-300"
                                style={{ backgroundColor: color.hex }}
                              />
                              <span className="text-sm font-medium">
                                {color.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedModalProduct.isSizeAvailable && selectedModalProduct.sizes && selectedModalProduct.sizes.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">
                          Size:
                        </h3>
                        <div className="flex gap-2 flex-wrap">
                          {selectedModalProduct.sizes.map((size, index) => (
                            <span
                              key={index}
                              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium"
                            >
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        handleAddRelatedProduct(selectedModalProduct);
                        handleCloseProductModal();
                      }}
                      className={`w-full py-3 px-6 rounded-lg text-base font-bold transition-all ${selectedProducts.some(p => p.productId === selectedModalProduct.productId)
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }`}
                    >
                      {selectedProducts.some(p => p.productId === selectedModalProduct.productId)
                        ? 'Remove from Cart'
                        : 'Add to Cart'
                      }
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Special Iconic Text */}
        {landingData.specialIconicText && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 py-8 border-b border-gray-200">
            <div className="max-w-4xl mx-auto px-4">
              <div className="flex items-center gap-4 justify-center">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-red-600 rounded-full opacity-20 animate-ping"></div>
                </div>

                <p className="text-lg sm:text-xl lg:text-2xl text-gray-900 font-semibold text-center">
                  {landingData.specialIconicText}
                </p>

                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="absolute -inset-1 bg-red-600 rounded-full opacity-20 animate-ping"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Long Description Section */}
        {landingData.longDescription && (
          <div className="bg-white py-8 sm:py-12 border-b border-gray-200">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Product Details
              </h2>
              <div
                className="text-sm sm:text-base text-gray-700 leading-relaxed"
                style={{
                  lineHeight: '1.6',
                  maxHeight: showFullDescription ? 'none' : '9.6em',
                  overflow: 'hidden',
                  position: 'relative'
                }}
                dangerouslySetInnerHTML={{
                  __html: showFullDescription ? landingData.longDescription : getTruncatedDescription(landingData.longDescription)
                }}
              />

              {landingData.longDescription && landingData.longDescription.split('\n').filter(l => l.trim()).length > 6 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="mt-4 text-blue-600 hover:text-blue-800 hover:underline font-normal text-sm"
                >
                  {showFullDescription ? 'See less' : 'See more'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Description Images */}
        {landingData.descriptionImageUrls && landingData.descriptionImageUrls.length > 0 && (
          <div className="bg-white py-8 sm:py-12 border-b border-gray-200">
            <div className="max-w-4xl mx-auto px-4">
              <div className="space-y-4 sm:space-y-6">
                {landingData.descriptionImageUrls.map((imageUrl, index) => (
                  <div key={index} className="w-full">
                    <img
                      src={imageUrl}
                      alt={`Product detail ${index + 1}`}
                      className="w-full h-auto object-contain rounded-lg border border-gray-200"
                      style={{ maxHeight: '800px' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Customer Review Images - Mobile Screenshot Style */}
        {landingData.customerReviewImageUrls && landingData.customerReviewImageUrls.length > 0 && (
          <div className="bg-gray-50 py-8 sm:py-12 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Customer Reviews
              </h2>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {landingData.customerReviewImageUrls.map((imageUrl, index) => (
                  <div key={index} className="relative overflow-hidden rounded-lg bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow group">
                    <img
                      src={imageUrl}
                      alt={`Customer review ${index + 1}`}
                      className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      style={{ aspectRatio: '9/16' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Customer Review Text Cards */}
        {landingData.customerReviews && landingData.customerReviews.length > 0 && (
          <div className="bg-white py-8 sm:py-12 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                What Our Customers Say
              </h2>

              <div
                ref={reviewScrollRef}
                className="flex gap-4 sm:gap-6 overflow-x-hidden"
                style={{ scrollBehavior: 'smooth' }}
              >
                {[...landingData.customerReviews, ...landingData.customerReviews].map((review, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 w-72 sm:w-80 lg:w-96 bg-white border-2 border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                          {review}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* YouTube Video Section */}
        {landingData.youtubeUrl && getYouTubeEmbedUrl(landingData.youtubeUrl) && (
          <div className="bg-gray-50 py-8 sm:py-12 border-b border-gray-200">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Product Video
              </h2>
              <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  src={getYouTubeEmbedUrl(landingData.youtubeUrl)}
                  title="Product Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-lg border border-gray-200"
                />
              </div>
            </div>
          </div>
        )}

        {/* Order Form Section - 2 Column Layout on Desktop */}
        <div ref={checkoutRef} className="bg-gray-50 py-8 sm:py-12 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8 text-center" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Complete Your Order
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 border border-gray-200">
                  <form onSubmit={handleOrderSubmit} className="space-y-6">
                    {/* Customer Info */}
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Enter your name"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Mobile Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="01XXXXXXXXX"
                        />
                        <p className={`text-xs mt-1 ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                          {message}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="division-select" className="block text-sm font-medium text-gray-700 mb-2">
                            Division *
                          </label>
                          <select
                            id="division-select"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            value={selectedDivisionId}
                            onChange={handleDivisionChange}
                            required
                          >
                            <option value="">Select Division</option>
                            {divisions.map((division) => (
                              <option key={division.id} value={division.id}>
                                {division.bn_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="district-select" className="block text-sm font-medium text-gray-700 mb-2">
                            District *
                          </label>
                          <select
                            id="district-select"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            value={selectedDistrictId}
                            onChange={handleDistrictChange}
                            disabled={!selectedDivisionId}
                            required
                          >
                            <option value="">Select District</option>
                            {districts.map((district) => (
                              <option key={district.id} value={district.id}>
                                {district.bn_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                          Full Address *
                        </label>
                        <textarea
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          required
                          rows="3"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                          placeholder="House/Flat No, Road, Area"
                        ></textarea>
                      </div>

                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                          Order Notes (Optional)
                        </label>
                        <textarea
                          id="notes"
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                          placeholder="Any special instructions"
                        ></textarea>
                      </div>
                    </div>

                    {/* Delivery Options */}
                    {/* <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                        Delivery Options *
                      </h3>
                      <div className="space-y-3">
                        <label
                          className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${deliveryOption === 'insideDhaka'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                            }`}
                        >
                          <input
                            type="radio"
                            name="deliveryOption"
                            value="insideDhaka"
                            checked={deliveryOption === 'insideDhaka'}
                            onChange={() => handleDeliveryOptionChange('insideDhaka')}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            Inside Dhaka - ৳{landingData.insideDhaka}
                          </span>
                        </label>

                        <label
                          className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${deliveryOption === 'outsideDhaka'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                            }`}
                        >
                          <input
                            type="radio"
                            name="deliveryOption"
                            value="outsideDhaka"
                            checked={deliveryOption === 'outsideDhaka'}
                            onChange={() => handleDeliveryOptionChange('outsideDhaka')}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            Outside Dhaka - ৳{landingData.outsideDhaka}
                          </span>
                        </label>

                        <label
                          className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${deliveryOption === 'subarea'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                            }`}
                        >
                          <input
                            type="radio"
                            name="deliveryOption"
                            value="subarea"
                            checked={deliveryOption === 'subarea'}
                            onChange={() => handleDeliveryOptionChange('subarea')}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            Sub-area - ৳{landingData.subarea}
                          </span>
                        </label>
                      </div>
                    </div> */}

                    {/* Payment Method */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                        Payment Method *
                      </h3>
                      <div className="space-y-3">
                        <label
                          className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedPaymentMethod === 'cashOnDelivery'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                            }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="cashOnDelivery"
                              checked={selectedPaymentMethod === 'cashOnDelivery'}
                              onChange={() => handlePaymentMethodChange('cashOnDelivery')}
                              className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900">
                              Cash on Delivery
                            </span>
                          </div>
                          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                            <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                          </svg>
                        </label>

                        <label
                          className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedPaymentMethod === 'bkash'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                            }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="bkash"
                              checked={selectedPaymentMethod === 'bkash'}
                              onChange={() => handlePaymentMethodChange('bkash')}
                              className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900">
                              bKash
                            </span>
                          </div>
                          <img
                            src="https://www.logo.wine/a/logo/BKash/BKash-bKash-Logo.wine.svg"
                            alt="bKash"
                            className="h-8 w-auto object-contain"
                          />
                        </label>

                        <label
                          className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedPaymentMethod === 'nagad'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                            }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="nagad"
                              checked={selectedPaymentMethod === 'nagad'}
                              onChange={() => handlePaymentMethodChange('nagad')}
                              className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900">
                              Nagad
                            </span>
                          </div>
                          <img
                            src="https://www.logo.wine/a/logo/Nagad/Nagad-Logo.wine.svg"
                            alt="Nagad"
                            className="h-8 w-auto object-contain"
                          />
                        </label>
                      </div>

                      {(selectedPaymentMethod === 'bkash' || selectedPaymentMethod === 'nagad') && paymentDetailsConfirmed && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm font-medium text-green-800 mb-1">
                            Payment Details Submitted
                          </p>
                          <p className="text-sm text-gray-700">
                            TrxID: <strong>{bkashTrxId}</strong>
                          </p>
                          <p className="text-sm text-gray-700">
                            Phone: <strong>{bkashPhoneNo}</strong>
                          </p>
                        </div>
                      )}
                    </div>

                    {mainFormError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-600">
                          {mainFormError}
                        </p>
                      </div>
                    )}

                    {/* Submit Button - Mobile Only */}
                    <button
                      type="submit"
                      disabled={!isOrderButtonEnabled || isOrderPlacing}
                      className={`lg:hidden w-full py-4 rounded-lg font-bold text-lg transition-all ${isOrderButtonEnabled && !isOrderPlacing
                        ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                      {isOrderPlacing ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing Order...
                        </span>
                      ) : (
                        'Confirm Order'
                      )}
                    </button>
                  </form>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 sticky top-24">
                  <h3 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    Order Summary
                  </h3>

                  {/* Selected Products */}
                  {selectedProducts.length > 0 && (
                    <div className="mb-6 space-y-3 max-h-64 overflow-y-auto">
                      {selectedProducts.map((selectedProduct) => (
                        <div
                          key={selectedProduct.productId}
                          className="flex items-start gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200"
                        >
                          <img
                            src={selectedProduct.image}
                            alt={selectedProduct.name}
                            className="w-12 h-12 object-contain rounded border border-gray-200 flex-shrink-0"
                          />

                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-semibold text-gray-900 line-clamp-2 mb-1">
                              {selectedProduct.name}
                            </h4>
                            <p className="text-xs text-gray-600">
                              ৳{selectedProduct.price} × {selectedProduct.selectedQuantity}
                            </p>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleQuantityChange(selectedProduct.productId, selectedProduct.selectedQuantity - 1)}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
                                disabled={selectedProduct.selectedQuantity <= 1}
                              >
                                -
                              </button>
                              <span className="w-6 text-center font-medium text-xs">
                                {selectedProduct.selectedQuantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(selectedProduct.productId, selectedProduct.selectedQuantity + 1)}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold"
                              >
                                +
                              </button>
                            </div>

                            <button
                              onClick={() => handleRemoveProduct(selectedProduct.productId)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Remove"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Price Summary */}
                  <div className="space-y-2 border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal ({selectedProducts.length} items):</span>
                      <span className="font-medium">৳{calculateSubtotal()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Delivery Charge:</span>
                      <span className="font-medium">{displayCharge}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total:</span>
                        <span className="text-orange-600">৳{calculateTotal()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button - Desktop Only */}
                  <button
                    onClick={handleOrderSubmit}
                    disabled={!isOrderButtonEnabled || isOrderPlacing}
                    className={`hidden lg:block w-full mt-6 py-4 rounded-lg font-bold text-lg transition-all ${isOrderButtonEnabled && !isOrderPlacing
                      ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    {isOrderPlacing ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing Order...
                      </span>
                    ) : (
                      'Confirm Order'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {selectedPaymentMethod === 'bkash' ? 'bKash' : 'Nagad'} Payment
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Please send payment to the number below and provide details:
            </p>
            <p className="text-lg font-bold text-gray-900 mb-4 bg-gray-100 p-3 rounded">
              Agent Number: {selectedPaymentMethod === 'bkash' ? bKashAgentNumber : nagadAgentNumber}
            </p>
            <input
              type="text"
              placeholder="Transaction ID (e.g., 8H7J6K5L)"
              value={bkashTrxId}
              onChange={(e) => setBkashTrxId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <input
              type="tel"
              placeholder="Your Mobile Number (11 digits)"
              value={bkashPhoneNo}
              onChange={(e) => setBkashPhoneNo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            {paymentModalError && (
              <p className="text-sm text-red-600 mb-3">
                {paymentModalError}
              </p>
            )}
            <button
              onClick={handlePaySubmit}
              className="w-full py-3 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors"
            >
              Complete Payment
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default LandingPage;
